import React, { useState, useEffect, useCallback } from 'react';
import { Upload, CheckCircle, FileText, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import FileDropzone, { UploadProgress } from './FileDropzone';
import DocumentListItem from './DocumentListItem';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTENT_TYPE_MAP = {
  'pdf': 'application/pdf',
  'jpg': 'image/jpeg',
  'jpeg': 'image/jpeg',
  'png': 'image/png',
  'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
};

const DocumentUploadSection = ({ 
  orderId, 
  orderStatus, 
  serviceType, 
  token,
  documentRequirements = [],
  onDocumentsChange 
}) => {
  const [requiredDocTypes, setRequiredDocTypes] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null); // Currently uploading doc type
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFileName, setUploadingFileName] = useState('');
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  // Determine if documents can be modified
  const canModifyDocuments = ['draft', 'documents_pending', 'payment_pending', 'payment_received'].includes(orderStatus);
  const showUploadUI = canModifyDocuments && requiredDocTypes.length > 0;

  // Fetch required document types and uploaded documents
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      
      try {
        const submissionsPromise = axios.get(`${API}/orders/${orderId}/submissions`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Prefer the product's own document_requirements (admin-configured) when
        // provided; otherwise fall back to the service-type document_types catalog.
        if (Array.isArray(documentRequirements) && documentRequirements.length > 0) {
          const mapped = documentRequirements.map((d, i) => ({
            id: d.doc_key || `doc_${i}`,
            type_name: d.doc_key || `doc_${i}`,
            display_name: d.name || d.doc_key || `Document ${i + 1}`,
            description: d.description || '',
            required: d.required !== false,
          }));
          const submissionsRes = await submissionsPromise;
          setRequiredDocTypes(mapped);
          setUploadedDocs(submissionsRes.data.submissions || []);
        } else {
          const [docTypesRes, submissionsRes] = await Promise.all([
            axios.get(`${API}/document-types`, {
              params: { service_type: serviceType },
              headers: { Authorization: `Bearer ${token}` }
            }),
            submissionsPromise
          ]);
          setRequiredDocTypes(docTypesRes.data.document_types || []);
          setUploadedDocs(submissionsRes.data.submissions || []);
        }
      } catch (err) {
        console.error('Error fetching document data:', err);
        setError('Failed to load document requirements');
      } finally {
        setLoading(false);
      }
    };

    if (orderId && serviceType && token) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, serviceType, token, documentRequirements.length]);

  // Check if a document type has been uploaded
  const getUploadedDocForType = useCallback((typeName) => {
    return uploadedDocs.find(doc => doc.document_type === typeName && !doc.is_deleted);
  }, [uploadedDocs]);

  // Handle file upload
  const handleFileSelect = async (file, docType) => {
    setUploading(docType.type_name);
    setUploadProgress(0);
    setUploadingFileName(file.name);
    setError('');

    try {
      // Get file extension and content type
      const ext = file.name.split('.').pop().toLowerCase();
      const contentType = CONTENT_TYPE_MAP[ext];
      
      if (!contentType) {
        throw new Error('Unsupported file type');
      }

      // Step 1: Get presigned URL
      setUploadProgress(10);
      const presignedRes = await axios.post(`${API}/upload/presigned`, {
        file_name: file.name,
        content_type: contentType,
        order_id: orderId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { upload_url, file_url, s3_key } = presignedRes.data;

      // Step 2: Upload file directly to S3
      setUploadProgress(30);
      await axios.put(upload_url, file, {
        headers: { 'Content-Type': contentType },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            30 + (progressEvent.loaded / progressEvent.total) * 50
          );
          setUploadProgress(percentCompleted);
        }
      });

      // Step 3: Save submission record
      setUploadProgress(85);
      const submissionRes = await axios.post(`${API}/orders/${orderId}/submissions`, {
        document_type: docType.type_name,
        file_name: file.name,
        file_url: file_url,
        s3_key: s3_key,
        file_size: file.size
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUploadProgress(100);

      // Update local state
      setUploadedDocs(prev => [...prev, submissionRes.data]);
      
      // Notify parent if callback provided
      onDocumentsChange?.();

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.detail || 'Failed to upload document. Please try again.');
    } finally {
      setTimeout(() => {
        setUploading(null);
        setUploadProgress(0);
        setUploadingFileName('');
      }, 500);
    }
  };

  // Handle document deletion
  const handleDelete = async (document) => {
    if (!window.confirm(`Remove "${document.file_name}"?`)) return;
    
    setDeletingId(document.id);
    setError('');

    try {
      await axios.delete(`${API}/orders/${orderId}/submissions/${document.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update local state
      setUploadedDocs(prev => prev.filter(doc => doc.id !== document.id));
      
      // Notify parent
      onDocumentsChange?.();
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.response?.data?.detail || 'Failed to remove document');
    } finally {
      setDeletingId(null);
    }
  };

  // Handle download — prefer 15-min presigned_url; fall back to file_url for
  // legacy documents that don't have an s3_key on the backend.
  const handleDownload = (document) => {
    const url = document.presigned_url || document.file_url;
    if (url) {
      window.open(url, '_blank');
    }
  };

  // Calculate completion status
  const allRequiredUploaded = requiredDocTypes.every(docType => 
    getUploadedDocForType(docType.type_name)
  );

  if (loading) {
    return (
      <div className="bg-[#111827] border border-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-center gap-3 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading documents...</span>
        </div>
      </div>
    );
  }

  // No documents required for this service
  if (requiredDocTypes.length === 0) {
    return (
      <div className="bg-[#111827] border border-gray-800 rounded-lg p-6" data-testid="no-docs-required">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center">
            <FileText className="w-5 h-5 text-gray-500" />
          </div>
          <div>
            <p className="text-white font-medium">No Documents Required</p>
            <p className="text-gray-500 text-sm">This service does not require document uploads.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#111827] border border-gray-800 rounded-lg p-6" data-testid="document-upload-section">
      <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
        <Upload className="w-5 h-5 text-[#059669]" />
        Required Documents
        {allRequiredUploaded && (
          <span className="ml-2 inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-[#059669]/10 text-[#059669]">
            <CheckCircle className="w-3 h-3" />
            Complete
          </span>
        )}
      </h2>

      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Document list */}
      <div className="space-y-4">
        {requiredDocTypes.map((docType) => {
          const uploadedDoc = getUploadedDocForType(docType.type_name);
          const isUploading = uploading === docType.type_name;
          
          return (
            <div key={docType.id} className="border border-gray-800 rounded-lg p-4">
              {/* Document type header */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-medium">{docType.display_name}</h3>
                    {docType.required && (
                      <span className="text-red-400 text-xs">Required</span>
                    )}
                    {uploadedDoc && (
                      <CheckCircle className="w-4 h-4 text-[#059669]" />
                    )}
                  </div>
                  <p className="text-gray-500 text-sm mt-1">{docType.description}</p>
                </div>
              </div>

              {/* Show upload progress */}
              {isUploading && (
                <UploadProgress progress={uploadProgress} fileName={uploadingFileName} />
              )}

              {/* Show uploaded document */}
              {uploadedDoc && !isUploading && (
                <DocumentListItem
                  document={uploadedDoc}
                  onDelete={canModifyDocuments ? handleDelete : null}
                  onDownload={handleDownload}
                  allowDelete={canModifyDocuments}
                  isDeleting={deletingId === uploadedDoc.id}
                />
              )}

              {/* Show upload dropzone if not uploaded and can modify */}
              {!uploadedDoc && !isUploading && canModifyDocuments && (
                <FileDropzone
                  onFileSelect={(file) => handleFileSelect(file, docType)}
                  disabled={!!uploading}
                />
              )}

              {/* Read-only message if can't modify */}
              {!uploadedDoc && !isUploading && !canModifyDocuments && (
                <div className="text-center py-4 text-gray-500 text-sm">
                  Document not uploaded
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* All done indicator */}
      {showUploadUI && allRequiredUploaded && (
        <div className="mt-6 p-4 bg-[#059669]/10 border border-[#059669]/20 rounded-lg">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-[#059669]" />
            <div>
              <p className="text-white font-medium">All Documents Uploaded</p>
              <p className="text-gray-400 text-sm">Your documents are being reviewed by our team.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentUploadSection;
